import { Component, Input, OnInit, input, signal } from '@angular/core';
import { TableModule , TableRowCollapseEvent, TableRowExpandEvent } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ProjectService } from '../../../../../../shared/services/project.service';
import { CommonModule } from '@angular/common';
import { ResponseProject } from '../response-tab/response.interface';
import { Role } from '../../project.interface';
import { TooltipModule } from 'primeng/tooltip';
import { CompanyService } from '../../../../../../shared/services/company.service';
import { PipesModule } from '../../../../../../shared/pipes/pipes.module';
import { HelperService } from '../../../../../../shared/services/helper.service';
import { CommonBaseClass } from '../../../../../../shared/classes/common-base-class';
import { ClientPortalService } from '../../../../../../shared/services/client-portal.service';
import { ClientPortalRole } from '../../../../../../shared/interfaces/client-portal.interface';

@Component({
  selector: 'app-details-tab',
  standalone: true,
  imports: [TableModule, ButtonModule, IconFieldModule, InputIconModule, CommonModule, TooltipModule, PipesModule],
  templateUrl: './details-tab.component.html',
  styleUrl: './details-tab.component.scss'
})
export class DetailsTabComponent extends CommonBaseClass implements OnInit {
  @Input() projectId!: any;
  /**
   * MP-5752: true for a CLIENT. The rows and the expanded sections both come from
   * the one client-portal roles call (MP-5749 item 4); the firm project, responses,
   * role-details and companies endpoints are never touched.
   */
  readonly clientMode = input(false);
  /** Client mode only: the portal roles, read on expand instead of a per-role fetch. */
  readonly portalRoles = signal<ClientPortalRole[]>([]);
  roleDetails!: Role;
  responseData: ResponseProject=[];
  expandedRows:{ [key: string]: boolean }  = {};
  projectDetails:any;
  projectSearchMarkets:string='';
  roleDetailsDisplay!:any;
  companies: any = [];

  constructor(private projectService: ProjectService, private companyService: CompanyService, helperService: HelperService,
    private clientPortalService: ClientPortalService) {
    super(helperService);
  }

  async ngOnInit() {
    if (this.clientMode()) {
      this.getClientPortalRoles();
      return;
    }
    this.getProjectDetails();
    this.getProjectResponse();
    this.companies = await this.companyService.getCompanies();
  }

  /** Client mode: one call feeds the rows and, on expand, the sections. */
  getClientPortalRoles(): void {
    if (!this.projectId) {
      return;
    }
    this.clientPortalService.getRoles(this.projectId).subscribe({
      next: (roles: ClientPortalRole[]) => {
        this.portalRoles.set(roles || []);
        this.responseData = (roles || []) as unknown as ResponseProject;
        if (roles?.length) {
          this.roleDetails = this.toRoleDetails(roles[0]);
        }
      }
    });
  }

  /**
   * Fills what the shared expansion template dereferences unconditionally, so a
   * portal role that omits a section (no documents, no education) renders "Any"
   * instead of throwing. Spread last so every field the backend sends wins.
   */
  toRoleDetails(role: ClientPortalRole): Role {
    const schedule = role.workSchedule?.schedule || {};
    const day = (value: { willWork?: boolean; time?: { start?: unknown; end?: unknown } } | undefined) =>
      ({ willWork: false, time: { start: null, end: null }, ...(value || {}) });
    return {
      jobType: [],
      degrees: [],
      criteria: [],
      credential: [],
      hasConflict: false,
      scanForConflicts: { fullCompanyNames: false, specificQuestions: false },
      conflictQuestions: [],
      ...role,
      workSchedule: {
        startAt: role.workSchedule?.startAt || '',
        duration: role.workSchedule?.duration || '',
        workHours: role.workSchedule?.workHours || '',
        schedule: {
          weekDays: day(schedule.weekDays),
          saturday: day(schedule.saturday),
          sunday: day(schedule.sunday)
        }
      }
    } as unknown as Role;
  }

  getProjectDetails(): void {
    if (this.projectId){
      this.projectService.getProjectById(this.projectId).subscribe({
        next: (resp) => {
          this.projectDetails = resp;
        }
      })
    }
  }

  onRowExpand(event: TableRowExpandEvent) {
    if (this.clientMode()) {
      const role = this.portalRoles().find((r) => r._id === event.data._id);
      if (role) {
        this.applyRoleDetails(this.toRoleDetails(role));
      }
    } else {
      this.projectService.getRoleDetails(this.projectId,event.data._id).subscribe((response)=>{
        this.applyRoleDetails(response);
      });
    }
    this.expandedRows = {};
    this.expandedRows[event.data._id] = true;  
  }

  applyRoleDetails(response: Role): void {
    this.roleDetails = response;
    this.roleDetailsDisplay = {
      jobType: this.roleDetails?.jobType,
      degrees: this.roleDetails?.degrees,
      graduate: this.roleDetails?.degrees.filter((degree) => degree.graduate),
      underGrad: this.roleDetails?.degrees.filter((degree) => !degree.graduate),
      skillsAndExperience: this.roleDetails?.criteria[0]?.labels?.values,
      credential: this.roleDetails?.credential,
    }
  }

  onRowCollapse(event: TableRowCollapseEvent) { }

  displaySearchMarkets(searchMarkets: any[]){
    return searchMarkets.length > 0 ? searchMarkets.map((s:any) => s.name).join(', ') : null;
  }

  getProjectResponse(): void {
    if (this.projectId){
      this.projectService.getProjectResponse(this.projectId).subscribe({
        next: (resp: ResponseProject) => {
          this.responseData = resp;
          if(this.responseData.length){
            this.projectService.getRoleDetails(this.projectId,this.responseData[0]?._id).subscribe((response)=>{
              this.roleDetails = response;
            })  
          }
        }
      })
     }
  }

  /**
   * Delegates to HelperService so the null-hours guard lives in one place; PWC roles carry
   * no working hours and the old local copy rendered them as "0null:0null null".
   */
  formatTime(time: { hour: number, min: number, meridiem: string } | null | undefined): string {
    return this.helperService.formatTime(time);
  }

  captionName(caption: any): string {
    return this.companies.filter((company: any) => company._id === caption)[0]?.name || caption
  }
}
